import { css } from '@emotion/css';
import styled from '@emotion/styled';
import posthog from '@leyan/lytics';
import { Checkbox, Tooltip } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import { Question } from 'views/Aide/Scenario/PolicyForm';
import { groupList, groupSubListMap } from './config';
import { Column, columns as _columns, skintificColumns } from './tableCol';
import { useTransAppProductName } from 'hooks/useTrans';
import { useOrgConfigStateValue } from '../../../../../hooks/business/useOrgConfig';

const Container = styled.div``;

const Section = styled.div`
  padding: 12px;
  background: #f7f9fa;
  & + & {
    margin-top: 12px;
  }
  > label {
    margin-bottom: 12px;
  }
`;

const Item = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 0;
  background: #fff;
  & + & {
    margin-top: 1px;
  }
  .label {
    flex: 0 0 120px;
    align-self: stretch;
    display: flex;
    flex-direction: row-reverse;
    align-items: center;
    width: 120px;
    padding: 0 14px;
    text-align: right;
    border-right: 1px solid #f0f2f5;
    color: #84868c;
  }
  .value {
    flex: 1 0 0;
    display: flex;
    flex-wrap: wrap;
    margin: 0 24px;
    color: #515359;
  }
`;
const checkboxItemStyle = `
  flex: 0 0 33.33333%;
  margin: 4px 0 !important;
  padding-right: 16px;
`;
const CheckboxItem = styled(Checkbox)`
  ${checkboxItemStyle}
`;

const CheckboxContent = styled.div`
  display: flex;
  > span {
    flex: 0 0 auto;
    margin-top: 4px;
    margin-left: 6px;
  }
  > div {
    word-break: break-word;
    white-space: pre-line;
    line-height: 22px;
  }
`;

interface Props {
  meta: Column[];
  setMeta: Dispatch<SetStateAction<Column[]>>;
}

const ColSelector = (props: Props) => {
  const { meta, setMeta } = props;
  const appProductNameT = useTransAppProductName();
  const { whiteListConfig: { SKINTIFICPERF } = {} } = useOrgConfigStateValue();
  const columns = useMemo(() => {
    return SKINTIFICPERF ? [..._columns, ...skintificColumns] : _columns;
  }, [SKINTIFICPERF]);
  const filteredColumnByGroup = useCallback(
    (group: Column['group'], subGroup: Column['subGroup']) => {
      return columns.filter((x) => x.group === group && x.subGroup === subGroup);
    },
    [columns],
  );

  const checkGroup = useCallback(
    (group: Column['group'], indeterminate?: boolean) => {
      const filteredByGroup = meta.filter((x) => x.group === group);
      if (indeterminate) {
        return filteredByGroup.some((x) => x.show) && !filteredByGroup.every((x) => x.show);
      }
      return filteredByGroup.every((x) => x.show);
    },
    [meta],
  );
  const checked = useCallback(
    (item: Column['key']) => meta.find((x) => x.key === item)?.show,
    [meta],
  );

  const traceFieldSelected = useCallback((column: Column) => {
    posthog.capture('$assistantPerformance', {
      type: 'fieldSelected',
      dataIndex: column.dataIndex,
      title: column.key,
    });
  }, []);

  const onGroupCheckChange = useCallback(
    (e: CheckboxChangeEvent, group: Column['group']) => {
      const { checked } = e.target;
      setMeta((pre) =>
        pre.map((x) => {
          if (x.group === group) {
            traceFieldSelected(x);

            return {
              ...x,
              show: checked,
              sortIndex: checked ? x.sortIndex : undefined,
            };
          }
          return x;
        }),
      );
    },
    [setMeta, traceFieldSelected],
  );
  const onChange = useCallback(
    (e: CheckboxChangeEvent, item: Column['key']) => {
      const { checked } = e.target;
      const target = columns.find((x) => x.key === item);
      setMeta((pre) => [
        ...pre.filter((x) => x.key !== item),
        { ...target!, show: checked, sortIndex: undefined },
      ]);

      if (target && checked) {
        traceFieldSelected(target);
      }
    },
    [setMeta, traceFieldSelected, columns],
  );

  return (
    <Container>
      {groupList.map((group, idx) => {
        const { [group]: subGroups } = groupSubListMap;
        const checkAll = (
          <Checkbox
            checked={checkGroup(group)}
            indeterminate={checkGroup(group, true)}
            onChange={(e) => {
              onGroupCheckChange(e, group);
            }}
          >
            {appProductNameT(`ViewDashboard.${group}`)}
          </Checkbox>
        );

        const columnRender = (item: Column) => {
          const { key, tooltip, col = 1 } = item;
          const num = col - 1;
          return (
            <>
              <CheckboxItem key={key} checked={checked(key)} onChange={(e) => onChange(e, key)}>
                <CheckboxContent>
                  <div>
                    {appProductNameT(`ViewDashboard.${key}`)}
                    {tooltip ? (
                      <Tooltip title={appProductNameT(`ViewDashboard.${tooltip}`)}>
                        <Question />
                      </Tooltip>
                    ) : null}
                  </div>
                </CheckboxContent>
              </CheckboxItem>

              {Array(num)
                .fill(num)
                .map(() => (
                  <div
                    className={css`
                      ${checkboxItemStyle}
                    `}
                  />
                ))}
            </>
          );
        };
        const sectionKey = `${group}_${idx}`;
        if (!subGroups) {
          return (
            <Section key={sectionKey}>
              {checkAll}
              <Item>
                <div className="value">
                  {filteredColumnByGroup(group, undefined).map(columnRender)}
                </div>
              </Item>
            </Section>
          );
        }

        return (
          <Section key={sectionKey}>
            {checkAll}
            {subGroups.map((sub) => {
              return (
                <Item key={sub}>
                  <div className="label">{appProductNameT(`ViewDashboard.${sub}`)}</div>
                  <div className="value">{filteredColumnByGroup(group, sub).map(columnRender)}</div>
                </Item>
              );
            })}
          </Section>
        );
      })}
    </Container>
  );
};

export default ColSelector;
